// Phase 13 — SE Payment capture (offline-safe)
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle2, MapPin, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDistributors, compressAndUploadPhoto, todayIsoDate, idempKey, useGps, StatusPill } from "./_helpers";

const MODES = [
  { code: "cash", label: "Cash" }, { code: "cheque", label: "Cheque" },
  { code: "upi", label: "UPI" }, { code: "neft", label: "NEFT / RTGS" },
  { code: "other", label: "Other" },
];

export default function PaymentNew() {
  const nav = useNavigate();
  const dists = useDistributors();
  const gps = useGps();
  const [distributorId, setDistributorId] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("upi");
  const [ref, setRef] = useState("");
  const [payDate, setPayDate] = useState(todayIsoDate());
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function uploadPhoto(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const p = await compressAndUploadPhoto(f);
      setPhoto(p); toast.success("Receipt uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  }

  async function submit() {
    if (!distributorId) return toast.error("Choose a distributor");
    if (!amount || Number(amount) <= 0) return toast.error("Amount must be > 0");
    if (mode !== "cash" && !ref.trim()) return toast.error("Reference required for this mode");
    setBusy(true);
    try {
      const res = await offlineMutate({
        method: "POST", path: "/payments", entity_type: "distributor_payments",
        body: {
          distributor_id: distributorId, amount: Number(amount), payment_mode: mode,
          reference_no: ref.trim() || null, payment_date: payDate,
          receipt_photo_ref: photo, notes: notes || null, gps,
          client_ts: new Date().toISOString(),
          idempotency_key: idempKey("pay"),
          offline_captured: !navigator.onLine,
        },
      });
      if (res.immediate) toast.success("Payment submitted");
      else toast.success("Queued — will sync when online");
      nav("/money/payments");
    } catch (e) {
      toast.error(e?.message || "Submit failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="payment-new-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="font-semibold flex-1">Record payment</h1>
      </div>

      <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-800 flex gap-2">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Never enter a bank account number or card number as reference. Use the UPI id, cheque number, or NEFT UTR.</span>
      </div>

      <div>
        <Label>Distributor *</Label>
        <Select value={distributorId || "__none__"} onValueChange={(v) => setDistributorId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="pay-distributor"><SelectValue placeholder="Choose distributor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {dists.map(d => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Amount (₹) *</Label>
        <Input data-testid="pay-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>

      <div>
        <Label>Mode *</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
          <SelectContent>{MODES.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div>
        <Label>Reference {mode !== "cash" && "*"}</Label>
        <Input data-testid="pay-reference" maxLength={30} placeholder="UPI txn id / cheque no / UTR"
               value={ref} onChange={(e) => setRef(e.target.value)} />
        <p className="text-[11px] text-neutral-500 mt-1">Max 30 chars. Card numbers &amp; bank account numbers are blocked.</p>
      </div>

      <div>
        <Label>Payment date *</Label>
        <Input data-testid="pay-date" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
      </div>

      <div>
        <Label>Receipt photo</Label>
        <input type="file" accept="image/*" capture="environment" className="hidden" id="pay-photo-input" onChange={uploadPhoto} />
        <label htmlFor="pay-photo-input" data-testid="pay-photo"
               className={`block w-full border rounded-md p-3 text-center cursor-pointer ${photo ? "bg-emerald-50 border-emerald-500" : "bg-white border-dashed border-neutral-300"}`}>
          {photo ? <span className="text-emerald-700 text-xs flex justify-center items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Captured</span>
                 : <span className="text-neutral-500 text-sm flex justify-center items-center gap-1"><Camera className="w-4 h-4" /> Tap to capture</span>}
        </label>
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea data-testid="pay-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {gps && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800 flex items-center gap-2">
          <MapPin className="w-3 h-3" />
          GPS captured: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (±{Math.round(gps.accuracy)}m)
        </div>
      )}

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy}
              data-testid="pay-submit">
        <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit payment"}
      </Button>
    </div>
  );
}

// -------------------- Payments list (SE view) --------------------
export function PaymentsList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/payments?page_size=100").then(r => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="payments-list-page">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-lg">Payments</h1>
        <Button size="sm" data-testid="payments-new-btn" onClick={() => nav("/money/payments/new")}
                className="bg-emerald-600 hover:bg-emerald-700">+ New payment</Button>
      </div>
      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 text-sm border border-dashed rounded-lg p-6 text-center">No payments yet</div>
       : items.map(p => (
         <div key={p.id} className="border rounded-lg p-3 bg-white" data-testid={`payment-row-${p.id}`}>
           <div className="flex justify-between items-start">
             <div className="min-w-0">
               <div className="font-medium truncate">{p.distributor_name_snapshot || "—"}</div>
               <div className="text-xs text-neutral-500">{p.payment_mode.toUpperCase()} · {p.reference_no || "—"}</div>
               <div className="text-xs text-neutral-400 mt-1">v{p.version} · {p.payment_date}</div>
             </div>
             <div className="text-right">
               <div className="font-semibold">₹ {Number(p.amount).toLocaleString("en-IN")}</div>
               <StatusPill status={p.status} />
             </div>
           </div>
           {p.reviewer_notes && <div className="mt-2 text-[11px] text-neutral-600 bg-neutral-50 border rounded p-2">📝 {p.reviewer_notes}</div>}
         </div>
       ))
      }
    </div>
  );
}
